<template>
  <div id="member-subscription-list" class="premium-dashboard">
    <!-- Background Floating Light Orbs -->
    <div class="glow-orb orb-1"></div>
    <div class="glow-orb orb-2"></div>

    <!-- Massive Hero Dashboard Header -->
    <v-card class="hero-card mb-6" flat>
      <div class="hero-glow-effect"></div>

      <!-- Floating Decorative Ambient Icons with adjusted visibility and positions -->
      <v-icon class="deco-icon icon-card" color="rgba(255, 255, 255, 0.08)"
        >mdi-credit-card-outline</v-icon
      >
      <v-icon class="deco-icon icon-receipt" color="rgba(255, 255, 255, 0.08)"
        >mdi-receipt-text-outline</v-icon
      >
      <v-icon class="deco-icon icon-safe" color="rgba(255, 255, 255, 0.08)"
        >mdi-shield-check-outline</v-icon
      >
      <v-icon class="deco-icon icon-coin" color="rgba(255, 255, 255, 0.08)"
        >mdi-currency-inr</v-icon
      >

      <!-- Ocean Wave Background Animation -->
      <div class="waves-container">
        <svg
          class="waves"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          viewBox="0 24 150 28"
          preserveAspectRatio="none"
          shape-rendering="auto"
        >
          <defs>
            <path
              id="gentle-wave"
              d="M-160 44c30 0 58-18 88-18s58 18 88 18 58-18 88-18 58 18 88 18v44h-352z"
            />
          </defs>
          <g class="parallax">
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="0"
              fill="rgba(255, 255, 255, 0.05)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="3"
              fill="rgba(255, 255, 255, 0.08)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="5"
              fill="rgba(255, 255, 255, 0.03)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="7"
              fill="rgba(255, 255, 255, 0.07)"
            />
          </g>
        </svg>
      </div>

      <br />
      <v-row class="ma-0 align-center relative-content">
        <v-col cols="12" md="7" class="pa-6 pb-12">
          <div class="d-flex align-center title-container">
            <v-icon large color="white" class="mr-3 title-icon pulse-animation"
              >mdi-wallet-membership</v-icon
            >
            <h1 class="hero-title animate-title">JCSAT Kit Payment Center</h1>
          </div>
          <p class="hero-subtitle animate-subtitle">
            Track, manage and monitor all JCSAT kit purchases and online
            payments.
          </p>
        </v-col>
        <v-col
          cols="12"
          md="5"
          class="pa-6 pb-12 text-md-right d-none d-sm-block"
        >
          <div
            class="quick-stat-pill d-inline-flex align-center px-4 py-2 mr-3 pulse-border"
          >
            <v-icon color="#4caf50" class="mr-2 spin-pulse-icon"
              >mdi-checkbox-marked-circle</v-icon
            >
            <div class="text-left">
              <span class="pill-label">Live Gateway</span>
              <span class="pill-val">Razorpay Active</span>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- Summary Statistics Row -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-total"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Total Orders</span>
              <h2 class="stat-value">{{ totalOrders }}</h2>
              <div
                class="stat-sublabel text-caption primary--text font-weight-bold mt-1 d-flex align-center"
              >
                <v-icon x-small color="#1565C0" class="mr-1"
                  >mdi-bookshelf</v-icon
                >
                <span
                  >{{ totalKits }}
                  {{ totalKits === 1 ? "Kit" : "Kits" }} Purchased</span
                >
              </div>
            </div>
            <v-avatar color="rgba(21, 101, 192, 0.1)" size="48">
              <v-icon color="#1565C0">mdi-package-variant-closed</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-rev"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Revenue</span>
              <h2 class="stat-value">
                ₹{{
                  totalRevenue.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}
              </h2>
            </div>
            <v-avatar color="rgba(76, 175, 80, 0.1)" size="48">
              <v-icon color="#4caf50">mdi-currency-inr</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-success"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Successful Payments</span>
              <h2 class="stat-value">{{ successfulPayments }}</h2>
            </div>
            <v-avatar color="rgba(30, 136, 229, 0.1)" size="48">
              <v-icon color="#1e88e5">mdi-check-circle-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-pending"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Pending Payments</span>
              <h2 class="stat-value">{{ pendingPayments }}</h2>
            </div>
            <v-avatar color="rgba(255, 152, 0, 0.1)" size="48">
              <v-icon color="#ff9800">mdi-alert-circle-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Floating Action Button for admin payment creation -->
    <v-container class="py-0" v-if="RoleId == 2">
      <v-btn
        fab
        dark
        fixed
        bottom
        right
        large
        class="floating-payment-btn"
        @click.prevent="addRecordPrompt = true"
      >
        <v-icon>mdi-credit-card-plus</v-icon>
      </v-btn>
    </v-container>

    <!-- Payment Capture dialog prompt -->
    <v-dialog v-model="addRecordPrompt" persistent max-width="600px">
      <add-jcsat-online-payment
        pageDescription="Add JCSAT Online Payment"
        :addRecordPrompt="addRecordPrompt"
        @hideAddRecordPrompt="hideAddRecordPrompt"
        v-if="addRecordPrompt"
      ></add-jcsat-online-payment>
    </v-dialog>

    <!-- JCSAT Order Preview Dialog -->
    <v-dialog
      v-model="previewDialog"
      content-class="preview-dialog-container"
      max-width="1200px"
    >
      <jcsat-order-preview
        :order-data="selectedOrder"
        :is-dialog="true"
        @close="previewDialog = false"
        v-if="previewDialog"
      ></jcsat-order-preview>
    </v-dialog>

    <!-- Filters Section -->
    <v-card class="filter-card glass-card mb-6">
      <div class="filter-card-header d-flex align-center">
        <v-icon color="white" class="mr-2">mdi-filter-variant</v-icon>
        <h3 class="filter-card-title">Advanced Filters</h3>
      </div>

      <v-card-text class="pa-6">
        <v-form ref="form2" v-model="valid1" lazy-validation>
          <v-row>
            <!-- Zone Filter -->
            <v-col cols="12" sm="6" md="4" lg="2.4" class="py-2">
              <label class="filter-label">Zone</label>
              <v-autocomplete
                @change="getLomCodeOptions"
                :reduce="(option) => option.value"
                :loading="ZoneCodeOptionsLoading"
                :items="ZoneCodeOptions"
                v-model="ZoneCode"
                placeholder="Select a Zone"
                dense
                clearable
                outlined
                rounded
                class="premium-select"
              ></v-autocomplete>
            </v-col>

            <!-- LOM Filter -->
            <v-col cols="12" sm="6" md="4" lg="2.4" class="py-2">
              <label class="filter-label">LO</label>
              <v-autocomplete
                :reduce="(option) => option.value"
                :loading="LomCodeOptionsLoading"
                :items="LomCodeOptions"
                v-model="LomCode"
                placeholder="Select an LO"
                dense
                clearable
                outlined
                rounded
                class="premium-select"
              ></v-autocomplete>
            </v-col>

            <!-- Payment ID Filter -->
            <v-col cols="12" sm="6" md="4" lg="2.4" class="py-2">
              <label class="filter-label">Payment ID</label>
              <v-text-field
                v-model="PaymentId"
                :rules="PaymentIdRules"
                placeholder="Razorpay Payment ID"
                dense
                outlined
                rounded
                class="premium-select"
              ></v-text-field>
            </v-col>

            <!-- From Date -->
            <v-col cols="12" sm="6" md="4" lg="2.4" class="py-2">
              <label class="filter-label">From Date</label>
              <v-menu
                v-model="menu1"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    required
                    v-model="FromDate"
                    label="From Date"
                    readonly
                    v-on="on"
                    dense
                    outlined
                    rounded
                    prepend-inner-icon="mdi-calendar"
                    class="premium-select"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="FromDate"
                  @input="menu1 = false"
                  color="#1565C0"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <!-- To Date -->
            <v-col cols="12" sm="6" md="4" lg="2.4" class="py-2">
              <label class="filter-label">To Date</label>
              <v-menu
                v-model="menu2"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    required
                    v-model="ToDate"
                    label="To Date"
                    readonly
                    v-on="on"
                    dense
                    outlined
                    rounded
                    prepend-inner-icon="mdi-calendar"
                    class="premium-select"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="ToDate"
                  @input="menu2 = false"
                  color="#1565C0"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <v-row class="mt-4 align-center">
            <v-col cols="12" class="text-center">
              <v-btn
                outlined
                rounded
                large
                class="filter-btn btn-clear mr-4"
                @click.prevent="clearFilters"
              >
                <v-icon left>mdi-filter-remove-outline</v-icon>
                Reset Filters
              </v-btn>
              <v-btn
                :disabled="!valid1"
                @click.prevent="searchForm"
                :loading="SearchFlag"
                rounded
                large
                class="filter-btn btn-search white--text"
              >
                <v-icon left>mdi-magnify</v-icon>
                Search Payments
              </v-btn>
              <v-btn
                v-if="RoleId == 2"
                @click.prevent="addRecordPrompt = true"
                rounded
                large
                class="filter-btn btn-add-payment white--text ml-4"
              >
                <v-icon left>mdi-credit-card-plus</v-icon>
                Add Online Payment
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Loading Shimmer State -->
    <v-card
      v-if="SearchFlag || StartupLoadingFlag"
      class="glass-card pa-6 mb-6"
    >
      <v-skeleton-loader
        type="table-heading, table-thead, table-tbody, table-tfoot"
        class="premium-loader"
      ></v-skeleton-loader>
    </v-card>

    <!-- No Records Empty State -->
    <v-card
      v-if="
        !StartupLoadingFlag &&
        !SearchFlag &&
        ResultFlag &&
        tableData1.length == 0
      "
      class="glass-card py-12 text-center"
    >
      <div class="empty-state-graphic mb-4">
        <v-avatar color="rgba(21, 101, 192, 0.08)" size="120">
          <v-icon size="64" color="#1565C0">mdi-text-box-search-outline</v-icon>
        </v-avatar>
      </div>
      <h3 class="empty-state-title">
        No payment records found for selected filters
      </h3>
      <p class="empty-state-subtitle">
        Try adjusting your search criteria, dates, or resetting the filters.
      </p>
      <v-btn
        outlined
        rounded
        color="#1565C0"
        class="mt-4"
        @click.prevent="clearFilters"
      >
        Reset Search Filters
      </v-btn>
    </v-card>

    <!-- Data Table & Search Grid -->
    <v-card
      v-if="!StartupLoadingFlag && !SearchFlag && tableData1.length > 0"
      class="table-container-card glass-card"
    >
      <v-card-title
        class="px-6 py-4 d-flex justify-content-between align-center flex-wrap"
      >
        <div class="d-flex align-center mb-2 mb-sm-0">
          <v-icon color="#1565C0" class="mr-2">mdi-database</v-icon>
          <span class="result-count-text"
            >{{ tableData1.length }} Payments Found</span
          >
        </div>

        <div class="d-flex align-center flex-wrap">
          <!-- Excel Export Action -->
          <v-btn
            v-if="DownloadFlag"
            class="export-btn mr-4 px-4 py-2"
            @click.prevent="
              generateExcel(
                tableData1,
                tableOptions1.ExcelFields,
                tableOptions1.ExcelFileName,
                'My Worksheet'
              )
            "
          >
            <v-icon left color="white">mdi-file-excel</v-icon>
            Export Sheet
          </v-btn>

          <!-- Table Search Bar -->
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            placeholder="Quick search table..."
            single-line
            hide-details
            dense
            outlined
            rounded
            class="global-table-search"
          ></v-text-field>
        </div>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-data-table
          class="premium-table"
          loading-text="Processing datasets..."
          v-model="selected"
          :search="search"
          :loading="TableLoadingFlag"
          :item-key="tableOptions1.ItemKey"
          :single-select="tableOptions1.SingleSelectFlag"
          :headers="tableColumns1"
          :items="tableData1"
          :items-per-page="tableOptions1.ItemsPerPage"
          :footer-props="tableOptions1.FooterProps"
        >
          <!-- Custom renderers for premium cells -->
          <template v-slot:item.OrdersDateTxt="{ item }">
            <span class="table-date-badge">
              <v-icon x-small color="#1565C0" class="mr-1"
                >mdi-calendar-range</v-icon
              >
              {{ item.OrdersDateTxt }}
            </span>
          </template>

          <template v-slot:item.ReceivedAmount="{ item }">
            <span class="table-amount-badge"
              >₹{{
                parseFloat(item.ReceivedAmount || 0).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              }}</span
            >
          </template>

          <template v-slot:item.RzrpayPaymentId="{ item }">
            <code class="payment-id-tag">{{
              item.RzrpayPaymentId || "N/A"
            }}</code>
          </template>

          <template v-slot:item.MemberFullname="{ item }">
            <div class="d-flex align-center py-1">
              <div class="text-left">
                <div class="member-name">{{ item.MemberFullname }}</div>
                <div class="member-id-subtitle text-caption text-muted">
                  {{ item.MembershipId }}
                </div>
              </div>
            </div>
          </template>

          <template v-slot:item.KitDetails="{ item }">
            <div class="kit-desc-pill">
              <v-icon x-small color="#1565C0" class="mr-1"
                >mdi-bookshelf</v-icon
              >
              <span>{{ item.KitDetails }}</span>
            </div>
          </template>

          <template v-slot:item.PaymentStatus="{ item }">
            <v-chip
              small
              class="font-weight-bold status-chip"
              color="rgba(76, 175, 80, 0.1)"
              text-color="#2e7d32"
              v-if="item.RzrpayPaymentId"
            >
              <v-icon left x-small color="#2e7d32">mdi-check-circle</v-icon>
              Paid
            </v-chip>
            <v-chip
              small
              class="font-weight-bold status-chip"
              color="rgba(255, 152, 0, 0.1)"
              text-color="#e65100"
              v-else
            >
              <v-icon left x-small color="#e65100">mdi-clock-outline</v-icon>
              Pending
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="#1565C0"
                  class="action-btn-view mr-1"
                  v-bind="attrs"
                  v-on="on"
                  @click.prevent="openPreview(item)"
                >
                  <v-icon small>mdi-eye-outline</v-icon>
                </v-btn>
              </template>
              <span>View Details</span>
            </v-tooltip>

            <v-tooltip bottom v-if="item.ReceiptFilePath">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="#059669"
                  class="action-btn-download"
                  v-bind="attrs"
                  v-on="on"
                  @click.prevent="downloadReceipt(item)"
                >
                  <v-icon small>mdi-download-outline</v-icon>
                </v-btn>
              </template>
              <span>Download Receipt</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import AddJcsatOnlinePayment from "@/view/pages/erp/payment/AddJcsatOnlinePayment.vue";
import JcsatOrderPreview from "@/view/pages/erp/reports/jcsat-report/JcsatOrderPreview.vue";

export default {
  mixins: [common],
  components: {
    AddJcsatOnlinePayment,
    JcsatOrderPreview,
  },
  data() {
    return {
      valid1: true,
      LoadingFlag: false,
      TableLoadingFlag: false,
      StartupLoadingFlag: false,
      FloatingButtonFlag: false,
      SearchFlag: false,
      ResultFlag: false,

      DownloadFlag: false,
      ExcelFileName: "",
      ExcelFields: {},

      tableData1: [],
      tableColumns1: [],
      tableOptions1: {
        ItemKey: "jcsat_order_id",
        SingleSelectFlag: true,
        ItemsPerPage: 25,
        FooterProps: {
          "items-per-page-options": [25, 50, 75, 100, -1],
        },
      },
      selected: [],
      search: "",
      CurrentYearId: 0,

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      addRecordPrompt: false,
      previewDialog: false,
      selectedOrder: null,

      PaymentId: "",
      PaymentIdRules: [],

      ZoneCode: "",
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,

      LomCode: "",
      LomCodeOptions: [],
      LomCodeOptionsLoading: false,

      FromDate: "",
      menu1: false,

      ToDate: "",
      menu2: false,

      RoleId: "",
    };
  },
  computed: {
    totalOrders() {
      return this.tableData1 ? this.tableData1.length : 0;
    },
    totalKits() {
      if (!this.tableData1 || this.tableData1.length === 0) return 0;
      return this.tableData1.reduce((sum, item) => {
        let kitCount = 0;
        if (item.KitDetails) {
          const matches = [...item.KitDetails.matchAll(/\((\d+)\s*Qty\)/gi)];
          if (matches.length > 0) {
            kitCount = matches.reduce(
              (acc, m) => acc + (parseInt(m[1]) || 0),
              0
            );
          }
        }
        if (kitCount === 0) {
          kitCount = parseInt(item.OrderQty || item.OrderQuantity || 0) || 0;
        }
        return sum + kitCount;
      }, 0);
    },
    totalRevenue() {
      if (!this.tableData1 || this.tableData1.length === 0) return 0;
      return this.tableData1.reduce((sum, item) => {
        return sum + parseFloat(item.ReceivedAmount || 0);
      }, 0);
    },
    successfulPayments() {
      return this.tableData1
        ? this.tableData1.filter((item) => item.RzrpayPaymentId).length
        : 0;
    },
    pendingPayments() {
      return this.tableData1
        ? this.tableData1.filter((item) => !item.RzrpayPaymentId).length
        : 0;
    },
  },
  watch: {
    ZoneCodeOptions: function () {
      this.ZoneCodeOptionsLoading = false;
    },
    LomCodeOptions: function () {
      this.LomCodeOptionsLoading = false;
    },
  },
  methods: {
    clearFilters() {
      this.ZoneCode = "";
      this.LomCode = "";
      this.PaymentId = "";
      var todayDate = new Date().toISOString().slice(0, 10);
      this.FromDate = todayDate;
      this.ToDate = todayDate;
      this.resetForm();
    },
    hideAddRecordPrompt() {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      this.searchForm();
    },
    resetForm() {
      this.search = "";
      this.tableData1 = [];
      this.selected = [];
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.resetForm();

      var todayDate = new Date().toISOString().slice(0, 10);
      this.FromDate = todayDate;
      this.ToDate = todayDate;

      var RoleId = this.$session.get("RoleId");
      this.RoleId = RoleId == (null || undefined) ? "" : RoleId;

      this.getZoneCodeOptions();
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      this.ZoneCode = "";
      this.ZoneCodeOptionsLoading = true;
      var selectbox1_source = "ZoneCode";
      var selectbox1_destination = "ZoneCodeOptions";
      var selectbox1_url = "api/zone/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getLomCodeOptions() {
      console.log("getLomCodeOptions called");
      this.LomCode = "";
      this.LomCodeOptionsLoading = true;
      var selectbox1_source = "LomCode";
      var selectbox1_destination = "LomCodeOptions";
      var selectbox1_url = "api/year-wise-lom/lom-options";
      var selectbox1_conditions_array = {
        UserInterface: 2,
        ZoneCode: this.ZoneCode,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },

    searchForm() {
      console.log("searchForm is called");

      var validate1 = this.$refs.form2.validate();
      var FromDate = this.FromDate;
      var ToDate = this.ToDate;

      if (validate1 && FromDate != "" && ToDate != "") {
        this.DownloadFlag = false;
        this.ExcelFileName = "";
        this.SearchFlag = true;
        this.tableData1 = [];

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jcsat/order/lists";
        var upload = {
          UserInterface: 1,
          PaymentId: this.PaymentId,
          Zone: this.ZoneCode,
          Lom: this.LomCode,
          FromDate: this.FromDate,
          ToDate: this.ToDate,
        };

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then(function (response) {
            thisIns.SearchFlag = false;
            thisIns.ResultFlag = true;

            output = response.data.output;
            flag = response.data.flag;

            records = response.data.records;
            var options = records.TableOptions;
            thisIns.tableOptions1 = options;

            if (flag == 1) {
              var cols = response.data.records.TableHeader;
              if (!cols.find((c) => c.value === "PaymentStatus")) {
                var actionsIdx = cols.findIndex((c) => c.value === "actions");
                if (actionsIdx !== -1) {
                  cols.splice(actionsIdx, 0, {
                    text: "Status",
                    value: "PaymentStatus",
                  });
                } else {
                  cols.push({ text: "Status", value: "PaymentStatus" });
                }
              }
              thisIns.tableColumns1 = cols;
              thisIns.tableData1 = records.TableData;
              thisIns.DownloadFlag = options.DownloadFlag;
              thisIns.ExcelFileName = options.ExcelFileName;
              thisIns.ExcelFields = options.ExcelFields;

              thisIns.toast("success", output);
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SearchFlag = false;
          });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        if (FromDate == "") {
          message += "From Date should not be empty. ";
        }
        if (ToDate == "") {
          message += "To Date should not be empty. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    openPreview(item) {
      localStorage.setItem("selected_jcsat_order", JSON.stringify(item));
      this.selectedOrder = item;
      this.previewDialog = true;
    },
    downloadReceipt(item) {
      if (item.ReceiptFilePath) {
        window.open(item.ReceiptFilePath, "_blank");
      } else {
        this.sweetAlert("error", "Receipt file not found.", false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap");

.premium-dashboard {
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif;
  background-color: #f6f8fc;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  padding: 12px;
}

/* Floating light ambient background orbs */
.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  pointer-events: none;
  opacity: 0.45;
  z-index: 0;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, #42a5f5 0%, rgba(21, 101, 192, 0) 70%);
  top: -100px;
  right: -50px;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(
    circle,
    rgba(21, 101, 192, 0.2) 0%,
    rgba(66, 165, 245, 0) 70%
  );
  bottom: -150px;
  left: -100px;
}

/* Massive Hero Dashboard Header styling */
.hero-card {
  background: linear-gradient(
    135deg,
    #1565c0 0%,
    #1a73e8 45%,
    #42a5f5 100%
  ) !important;
  border-radius: 20px !important;
  color: white;
  box-shadow: 0 16px 40px rgba(21, 101, 192, 0.25) !important;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.8px;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.hero-subtitle {
  font-size: 14px;
  opacity: 0.95;
  margin-top: 6px;
  max-width: 600px;
  font-weight: 400;
}

.quick-stat-pill {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.pill-label {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
}

.pill-val {
  display: block;
  font-size: 13px;
  font-weight: 700;
}

/* Glassmorphism General Cards styling */
.glass-card {
  background: rgba(255, 255, 255, 0.82) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.7) !important;
  border-radius: 20px !important;
  box-shadow: 0 10px 30px rgba(33, 33, 33, 0.04) !important;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

/* Summary Statistics Cards */
.stat-card {
  padding: 24px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 15px 35px rgba(21, 101, 192, 0.12) !important;
}

.stat-card-glow {
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  filter: blur(40px);
  top: -40px;
  right: -40px;
  opacity: 0.15;
}

.orb-total {
  background-color: #1565c0;
}
.orb-rev {
  background-color: #4caf50;
}
.orb-success {
  background-color: #1e88e5;
}
.orb-pending {
  background-color: #ff9800;
}

.stat-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #78909c;
  letter-spacing: 0.8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 800;
  color: #263238;
  margin-top: 4px;
  letter-spacing: -0.5px;
}

/* Floating Action Button (CTA) style */
.floating-payment-btn {
  background: linear-gradient(135deg, #1565c0 0%, #1e88e5 100%) !important;
  box-shadow: 0 10px 30px rgba(21, 101, 192, 0.5) !important;
  z-index: 99;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.floating-payment-btn:hover {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 12px 35px rgba(21, 101, 192, 0.65) !important;
}

/* Advanced Filter Card header gradient */
.filter-card-header {
  background: linear-gradient(90deg, #1565c0 0%, #1e88e5 100%);
  padding: 16px 24px;
  color: white;
}

.filter-card-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 0;
}

.filter-label {
  font-size: 12px;
  font-weight: 700;
  color: #455a64;
  margin-bottom: 6px;
  display: block;
}

/* Autocomplete/Select box rounded controls */
.premium-select >>> .v-input__control > .v-input__slot {
  border-radius: 12px !important;
  border: 1.5px solid rgba(21, 101, 192, 0.1) !important;
  background-color: white !important;
  transition: all 0.3s ease !important;
}

.premium-select >>> .v-input__control > .v-input__slot:hover {
  border-color: rgba(21, 101, 192, 0.35) !important;
}

.premium-select.v-input--is-focused >>> .v-input__control > .v-input__slot {
  border-color: #1565c0 !important;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.15) !important;
}

/* Filter action buttons */
.filter-btn {
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.btn-clear {
  color: #455a64 !important;
  border: 1.5px solid rgba(69, 90, 100, 0.2) !important;
}

.btn-clear:hover {
  background-color: rgba(69, 90, 100, 0.05) !important;
  border-color: #455a64 !important;
}

.btn-search {
  background: linear-gradient(135deg, #1565c0 0%, #1e88e5 100%) !important;
  box-shadow: 0 6px 20px rgba(21, 101, 192, 0.3) !important;
  border: none !important;
}

.btn-search:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(21, 101, 192, 0.45) !important;
}

.btn-add-payment {
  background: linear-gradient(135deg, #1565c0 0%, #311b92 100%) !important;
  box-shadow: 0 6px 20px rgba(49, 27, 146, 0.4) !important;
  border: none !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.btn-add-payment:hover {
  transform: scale(1.04) translateY(-2px);
  box-shadow: 0 10px 25px rgba(49, 27, 146, 0.6) !important;
}

/* Skeleton Loaders and Empty States styling */
.premium-loader {
  background: transparent !important;
}

.empty-state-title {
  font-size: 18px;
  font-weight: 700;
  color: #37474f;
  margin-top: 16px;
}

.empty-state-subtitle {
  font-size: 13px;
  color: #78909c;
  max-width: 420px;
  margin: 6px auto 0;
}

/* Data grid container and table styles */
.table-container-card {
  overflow: hidden;
}

.result-count-text {
  font-size: 16px;
  font-weight: 700;
  color: #263238;
}

/* Export button styled as spreadsheet block */
.export-btn {
  background: linear-gradient(135deg, #2e7d32 0%, #4caf50 100%) !important;
  color: white !important;
  font-weight: 700 !important;
  text-transform: none !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 15px rgba(46, 125, 50, 0.25) !important;
  transition: all 0.3s ease !important;
}

.export-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(46, 125, 50, 0.4) !important;
}

/* Premium global table search text field */
.global-table-search {
  max-width: 280px;
}

.global-table-search >>> .v-input__control > .v-input__slot {
  border-radius: 12px !important;
  border: 1.5px solid rgba(21, 101, 192, 0.12) !important;
  background-color: rgba(21, 101, 192, 0.01) !important;
}

.global-table-search.v-input--is-focused
  >>> .v-input__control
  > .v-input__slot {
  border-color: #1565c0 !important;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.12) !important;
  background-color: white !important;
}

/* Real premium tables */
.premium-table >>> table {
  border-collapse: separate;
  border-spacing: 0;
}

.premium-table >>> thead > tr > th {
  background-color: rgba(21, 101, 192, 0.04) !important;
  color: #1565c0 !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  border-bottom: 2px solid rgba(21, 101, 192, 0.08) !important;
}

.premium-table >>> tbody > tr {
  transition: background-color 0.2s ease;
}

.premium-table >>> tbody > tr:hover {
  background-color: rgba(21, 101, 192, 0.03) !important;
}

.premium-table >>> tbody > tr > td {
  border-bottom: 1px solid rgba(21, 101, 192, 0.04) !important;
  font-size: 13px !important;
  color: #37474f;
  padding: 12px 16px !important;
}

/* Badges inside cells */
.table-date-badge {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  color: #1565c0;
  background: rgba(21, 101, 192, 0.06);
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 12px;
}

.table-amount-badge {
  font-weight: 700;
  color: #2e7d32;
  background: rgba(76, 175, 80, 0.08);
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 13px;
}

.payment-id-tag {
  font-family: "Courier New", Courier, monospace;
  font-weight: 700;
  color: #546e7a;
  background: rgba(84, 110, 122, 0.08);
  padding: 3px 6px;
  border-radius: 6px;
}

.avatar-initial {
  font-size: 12px;
  font-weight: 700;
  color: #1565c0;
}

.member-name {
  font-weight: 600;
  color: #263238;
}

.kit-desc-pill {
  display: inline-flex;
  align-items: flex-start;
  background: rgba(21, 101, 192, 0.04);
  padding: 4px 8px;
  border-radius: 8px;
  max-width: 280px;
  white-space: normal;
  word-break: break-word;
}

.kit-desc-pill .v-icon {
  margin-top: 2px;
}

.status-chip {
  font-weight: bold;
}

/* Ocean Wave Styling */
.waves-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40px;
  min-height: 40px;
  max-height: 60px;
  z-index: 1;
}

.waves {
  position: relative;
  width: 100%;
  height: 40px;
  margin-bottom: -7px;
}

.parallax > use {
  animation: move-forever 25s cubic-bezier(0.55, 0.5, 0.45, 0.5) infinite;
}

.parallax > use:nth-child(1) {
  animation-delay: -2s;
  animation-duration: 7s;
}

.parallax > use:nth-child(2) {
  animation-delay: -3s;
  animation-duration: 10s;
}

.parallax > use:nth-child(3) {
  animation-delay: -4s;
  animation-duration: 13s;
}

.parallax > use:nth-child(4) {
  animation-delay: -5s;
  animation-duration: 20s;
}

@keyframes move-forever {
  0% {
    transform: translate3d(-90px, 0, 0);
  }
  100% {
    transform: translate3d(85px, 0, 0);
  }
}

/* Hero header animations & shapes */
.hero-glow-effect {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  z-index: 0;
  pointer-events: none;
}

.hero-glow-effect::after {
  content: "";
  position: absolute;
  top: 0;
  left: -50%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.15) 30%,
    rgba(255, 255, 255, 0) 60%
  );
  transform: skewX(-25deg);
  animation: shineSweep 6s infinite ease-in-out;
}

.relative-content {
  position: relative;
  z-index: 2;
}

/* Decorative background icons */
.deco-icon {
  position: absolute !important;
  z-index: 1;
  pointer-events: none;
}

.icon-card {
  font-size: 110px !important;
  right: 20%;
  top: -20px;
  transform: rotate(15deg);
  animation: floatDeco 12s infinite ease-in-out;
}

.icon-receipt {
  font-size: 80px !important;
  left: 10%;
  bottom: -15px;
  transform: rotate(-10deg);
  animation: floatDeco 15s infinite ease-in-out alternate;
}

.icon-safe {
  font-size: 90px !important;
  right: 5%;
  bottom: -10px;
  transform: rotate(20deg);
  animation: floatDeco 10s infinite ease-in-out;
}

.icon-coin {
  font-size: 70px !important;
  left: 50%;
  top: -30px;
  opacity: 0.8;
  animation: floatDeco 14s infinite ease-in-out alternate;
}

/* Specific text and icon animations */
.animate-title {
  animation: slideInLeft 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-subtitle {
  opacity: 0;
  animation: slideInLeft 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.15s forwards;
}

.pulse-animation {
  animation: pulseIcon 3s infinite ease-in-out;
}

.spin-pulse-icon {
  animation: pulseGlowIcon 2s infinite ease-in-out;
}

.pulse-border {
  animation: borderGlow 4s infinite ease-in-out;
}

/* Keyframes definitions */
@keyframes shineSweep {
  0% {
    left: -120%;
  }
  15%,
  100% {
    left: 120%;
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes floatDeco {
  0%,
  100% {
    transform: translateY(0) rotate(15deg) scale(1);
  }
  50% {
    transform: translateY(-12px) rotate(20deg) scale(1.03);
  }
}

@keyframes pulseIcon {
  0%,
  100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0px rgba(255, 255, 255, 0));
  }
  50% {
    transform: scale(1.08);
    filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.6));
  }
}

@keyframes pulseGlowIcon {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
}

@keyframes borderGlow {
  0%,
  100% {
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  }
  50% {
    border-color: rgba(255, 255, 255, 0.45);
    box-shadow: 0 4px 25px rgba(255, 255, 255, 0.15);
  }
}
.action-btn-view {
  transition: transform 0.2s ease;
}

.action-btn-view:hover {
  transform: scale(1.18);
  background: rgba(21, 101, 192, 0.08) !important;
}

.preview-dialog-container {
  border-radius: 24px !important;
  overflow: hidden;
}
</style>
